// client/src/components/react-dashboard/data_assets/index.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Tabs,
  Tab,
  Paper,
  Grid,
  Stack,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import PulseLoader from 'react-spinners/PulseLoader';
import { Link } from 'react-router-dom';
import {
  useGetDataAssetsQuery,
  useGetDataAssetQuery,
} from '../../../features/data_assets/dataAssetsApiSlice';
import { tokens } from '../../../theme';
import AddVersionWidget from './AddVersionWidget';
import ReleaseDeliveryTab from './ReleaseDeliveryTab';
import EditDataAsset from './EditDataAsset';

/* ──────────────────────────────────────────────────────────
   SMALL UTILS
   ──────────────────────────────────────────────────────────*/
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-assets-tabpanel-${index}`}
      aria-labelledby={`data-assets-tab-${index}`}
      {...other}
    >
      {value === index && <Box pt={2}>{children}</Box>}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   DATA ASSET INFO 
   ──────────────────────────────────────────────────────────*/
const DataAssetInfo = ({ dataAsset, onUpdated }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  /* ─────── empty-state ───────────────────────── */
  if (!dataAsset) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          width: '100%',
          color: colors.grey[300],
          fontWeight: 'bold',
          py: 2,
        }}
      >
        No data asset selected
      </Box>
    );
  }

  /* ─────── status dot helper ─────────────────── */
  const getStatusLight = () => {
    const now = Date.now();
    const updateCadence = dataAsset.updateCadence || {};

    const lastTs = updateCadence.last_updated_timestamp_utc
      ? new Date(updateCadence.last_updated_timestamp_utc).getTime()
      : 0;

    const refreshRate = updateCadence.realTimeRefreshRate || 0;

    const offline =
      refreshRate === 0 ||
      Math.floor((now - lastTs) / 60000) > refreshRate;

    return (
      <Box display="flex" alignItems="center" mt={1} mb={0.5}>
        <Box
          width={10}
          height={10}
          borderRadius="50%"
          bgcolor={offline ? colors.redAccent[500] : colors.greenAccent[500]}
          mr={0.5}
        />
        <Typography variant="caption">
          {offline ? 'Offline' : 'Online'}
        </Typography>
      </Box>
    );
  };

  const headerChip = () => {
    const isStandard = dataAsset?.assetType === 'Standard Delivery';
    if (!isStandard) return <Chip size="small" label="Real-Time" />;

    if (!dataAsset?.release) return <Chip size="small" label="Standard (No Release)" />;

    return <Chip size="small" label={`Release: ${dataAsset.release}`} />;
  };

  /* ─────── DataGrid defaults ─────────────────── */
  const commonGridProps = {
    density: 'compact',
    headerHeight: 28,
    rowHeight: 28,
    hideFooterSelectedRowCount: true,
    disableColumnMenu: true,
    disableSelectionOnClick: true,
    rowsPerPageOptions: [10, 20, 50],
    pageSize: 10,
    sx: {
      fontSize: '0.75rem',
      '.MuiDataGrid-cell': { py: 0.5, px: 0.5 },
      '.MuiDataGrid-columnHeader': { py: 0.5, px: 0.5 },
    },
  };

  /* ─────── “Add Version” button (shared) ─────── */
  const addVersionBtn = (
    <Button
      size="small"
      variant="contained"
      sx={{
        backgroundColor: colors.greenAccent[500],
        color: colors.grey[900],
        '&:hover': { backgroundColor: colors.greenAccent[400] },
      }}
      onClick={() => setDialogOpen(true)}
    >
      Add Version
    </Button>
  );

  const editBtn = (
    <Button
      size="small"
      variant="contained"
      sx={{
        backgroundColor: colors.greenAccent[500],
        color: colors.grey[900],
        '&:hover': { backgroundColor: colors.greenAccent[400] },
      }}
      onClick={() => setEditOpen(true)}
    >
      Edit
    </Button>
  );

  /* ─────── Real-Time asset view ───────────────── */
  const renderRealTimeAsset = () => {
    const s3Cols = [
      { field: 'uri', headerName: 'S3 URI', flex: 1 },
      { field: 'description', headerName: 'Description', flex: 1 },
    ];
    const sfCols = [
      { field: 'table', headerName: 'Table', width: 100 },
      { field: 'description', headerName: 'Description', flex: 1 },
    ];

    const s3Rows = (dataAsset.s3_uris || []).map((u, i) => ({
      id: i,
      uri: u.uri,
      description: u.description || '',
    }));

    const sfRows = (dataAsset.snowflake_tables || []).map((t, i) => ({
      id: i,
      table: t.table,
      description: t.description || '',
    }));

    return (
      <>
        {/* ─── header row with button ─── */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', color: colors.blueAccent[400] }}
            >
              {dataAsset.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontStyle: 'italic', color: colors.grey[300] }}
            >
              {dataAsset.assetType} Asset — {dataAsset.usage}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {headerChip()}
            {addVersionBtn}
            {editBtn}
          </Stack>
        </Box>

        {getStatusLight()}

        <Typography variant="caption" sx={{ mb: 1 }}>
          Last updated: {dataAsset.updateCadence.last_updated_timestamp_utc}
          <br />
          Refresh Rate: {dataAsset.updateCadence.realTimeRefreshRate} min
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
              <Typography variant="caption" color={colors.primary[300]}>
                S3 URIs
              </Typography>
              <DataGrid
                rows={s3Rows}
                columns={s3Cols}
                {...commonGridProps}
                sx={{ ...commonGridProps.sx, height: 240 }}
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
              <Typography variant="caption" color={colors.primary[300]}>
                Snowflake Tables
              </Typography>
              <DataGrid
                rows={sfRows}
                columns={sfCols}
                {...commonGridProps}
                sx={{ ...commonGridProps.sx, height: 240 }}
              />
            </Paper>
          </Grid>
        </Grid>
      </>
    );
  };

  /* ─────── Counts / Versions / Rebuild (same logic) ─────── */
  const renderCountsStats = () => {
    const cols = [
      { field: 'name', headerName: 'Name', width: 100 },
      {
        field: 'value',
        headerName: 'Value',
        width: 90,
        valueFormatter: (p) => new Intl.NumberFormat().format(p.value),
      },
      { field: 'timeRecorded', headerName: 'Time', flex: 1 },
    ];
    const rows = (dataAsset.countsStats || []).map((s, i) => ({
      id: i,
      name: s.name,
      value: s.value,
      timeRecorded: s.timeRecorded,
    }));
    return (
      <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, mb: 1 }}>
        <Typography variant="caption" color={colors.primary[300]}>
          Counts Stats
        </Typography>
        <DataGrid
          rows={rows}
          columns={cols}
          {...commonGridProps}
          sx={{ ...commonGridProps.sx, height: 200 }}
        />
      </Paper>
    );
  };

  const renderVersions = () => {
    const cols = [
      {
        field: 'version',
        headerName: 'Ver',
        width: 90,
        minWidth: 80,
        resizable: true,
      },
      {
        field: 's3_uri_csv',
        headerName: 'S3 URI CSV',
        width: 280,
        minWidth: 180,
        resizable: true,
      },
      {
        field: 's3_uri_parquet',
        headerName: 'S3 URI Parquet',
        width: 280,
        minWidth: 180,
        resizable: true,
      },
      {
        field: 'snowflake_table',
        headerName: 'Snowflake Table',
        width: 260,
        minWidth: 180,
        resizable: true,
      },
      {
        field: 'timestamp',
        headerName: 'Added',
        width: 180,
        minWidth: 150,
        resizable: true,
      },
    ];

    const rows = (dataAsset.versions || []).map((v, i) => {
      const s3Locations = Array.isArray(v.s3_locations) ? v.s3_locations : [];

      const csvLoc = s3Locations.find((loc) => loc.format === 'csv');
      const parquetLoc = s3Locations.find((loc) => loc.format === 'parquet');

      return {
        id: i,
        version: v.version,
        s3_uri_csv: csvLoc?.s3_uri || '',
        s3_uri_parquet: parquetLoc?.s3_uri || '',
        snowflake_table: v.snowflake_table || '',
        timestamp: v.timestamp_utc
          ? new Date(v.timestamp_utc).toLocaleString()
          : 'N/A',
      };
    });

    return (
      <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, mb: 1 }}>
        <Typography variant="caption" color={colors.primary[300]}>
          Versions
        </Typography>
        <DataGrid
          rows={rows}
          columns={cols}
          {...commonGridProps}
          disableColumnSelector={false}
          sx={{
            ...commonGridProps.sx,
            height: 240,
            '& .MuiDataGrid-columnSeparator': {
              visibility: 'visible',
            },
          }}
        />
      </Paper>
    );
  };

  const renderRebuildInfo = () => {
    const rebuild = dataAsset.rebuild || {};
    return (
      <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, mb: 1 }}>
        <Typography variant="caption" color={colors.primary[300]}>
          Rebuild Info
        </Typography>
        {rebuild.isRebuilt != null && (
          <Typography variant="body2">Rebuilt: {String(rebuild.isRebuilt)}</Typography>
        )}
        {rebuild.frequency && <Typography variant="body2">Freq: {rebuild.frequency}</Typography>}
        {rebuild.time && <Typography variant="body2">Time: {rebuild.time}</Typography>}
        {rebuild.day && <Typography variant="body2">Day: {rebuild.day}</Typography>}
        {rebuild.dayOfWeek && <Typography variant="body2">Weekday: {rebuild.dayOfWeek}</Typography>}
        {rebuild.cronSchedule && <Typography variant="body2">Cron: {rebuild.cronSchedule}</Typography>}
      </Paper>
    );
  };

  /* ─────── Standard Delivery asset view ─────── */
  const renderStandardDeliveryAsset = () => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', color: colors.blueAccent[400] }}
          >
            {dataAsset.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontStyle: 'italic', color: colors.grey[300] }}
          >
            {dataAsset.assetType} Asset — {dataAsset.usage}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {headerChip()}
          {addVersionBtn}
          {editBtn}
        </Stack>
      </Box>

      {renderCountsStats()}
      {renderVersions()}
      {renderRebuildInfo()}
    </>
  );

  /* ─────── main wrapper ─────── */
  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 1,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflowY: 'auto',
      }}
    >
      {dataAsset.assetType === 'Real-Time' ? renderRealTimeAsset() : renderStandardDeliveryAsset()}

      {/* add versions dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <Box sx={{ p: 3 }}>
          <AddVersionWidget asset={dataAsset} onClose={() => setDialogOpen(false)} />
        </Box>
      </Dialog>
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <EditDataAsset
          asset={dataAsset}
          onClose={() => setEditOpen(false)}
          onSave={() => {
            onUpdated?.();     // ✅ forces immediate refresh
            setEditOpen(false);
          }}
        />
      </Dialog>
    </Paper>
  );
};

/* ──────────────────────────────────────────────────────────
   CONTRIBUTION STATS – placeholder implementation
   ──────────────────────────────────────────────────────────*/
const ContributionStats = ({ dataAssets }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!dataAssets?.length) {
    return (
      <Box textAlign="center" width="100%" py={4} color={colors.grey[300]}>
        No data assets available
      </Box>
    );
  }

  // Example simple aggregation – assets by usage
  const usageCounts = dataAssets.reduce((acc, a) => {
    acc[a.usage] = (acc[a.usage] || 0) + 1;
    return acc;
  }, {});

  const rows = Object.entries(usageCounts).map(([usage, count], i) => ({ id: i, usage, count }));
  const columns = [
    { field: 'usage', headerName: 'Usage', flex: 1 },
    { field: 'count', headerName: 'Count', flex: 1 },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Typography variant="h6" mb={1}>
        Contribution Stats (by Usage)
      </Typography>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        pageSize={5}
        hideFooterSelectedRowCount
        disableSelectionOnClick
      />
    </Paper>
  );
};

/* ──────────────────────────────────────────────────────────
   MAIN PAGE
   ──────────────────────────────────────────────────────────*/
const DataAssetManagement = () => {
  const [selectedDataAssetId, setSelectedDataAssetId] = useState(null);
  const [tab, setTab] = useState(0);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const {
    data: dataAssets,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetDataAssetsQuery(undefined, {
    pollingInterval: 15000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: selectedDataAsset,
    isFetching: isFetchingSelectedDataAsset,
    isError: isSelectedDataAssetError,
    error: selectedDataAssetError,
    refetch: refetchSelectedDataAsset,
  } = useGetDataAssetQuery(selectedDataAssetId, {
    skip: !selectedDataAssetId,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <PulseLoader color="#FFF" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="h6" color="error">
          Error fetching data assets: {error.message}
        </Typography>
      </Box>
    );
  }

  /* ─────── data processing ─────── */
  const da_ents = dataAssets?.entities || {};
  const allDataAssets = Object.values(da_ents);

  const realTimeServices = allDataAssets.filter((a) => a.assetType === 'Real-Time');
  const externalAssets = allDataAssets.filter(
    (a) => a.usage === 'External' && a.assetType !== 'Real-Time'
  );
  const internalAssets = allDataAssets.filter(
    (a) => a.usage === 'Internal' && a.assetType !== 'Real-Time'
  );

  /* ─────── column configs ─────── */
  const columnsRealTime = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'usage', headerName: 'Usage', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const { updateCadence } = params.row;
        const currentTimestamp = Date.now();
        const lastUpdatedTimestamp = updateCadence.last_updated_timestamp_utc
          ? new Date(updateCadence.last_updated_timestamp_utc).getTime()
          : 0;
        const diffMinutes = Math.floor((currentTimestamp - lastUpdatedTimestamp) / 60000);
        const offline = diffMinutes > updateCadence.realTimeRefreshRate;
        return (
          <Box display="flex" alignItems="center">
            <Box
              width={10}
              height={10}
              borderRadius="50%"
              bgcolor={offline ? 'red' : 'green'}
              mr={1}
            />
            {offline ? 'Offline' : 'Online'}
          </Box>
        );
      },
    },
  ];

  const columnsExternalInternal = [
    { field: 'name', headerName: 'Name', flex: 1 },

    // ✅ NEW: Release column
    {
      field: 'release',
      headerName: 'Release',
      width: 110,
      valueGetter: (params) => params.row.release || 'None',
    },

    {
      field: 'rebuild.isRebuilt',
      headerName: 'Rebuilt',
      flex: 1,
      renderCell: (params) => {
        const isRebuilt = params.row.rebuild?.isRebuilt;

        return (
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: isRebuilt ? 'green' : 'red',
            }}
          >
            {isRebuilt ? '✓' : '✗'}
          </span>
        );
      },
    },
    {
      field: 'latestVersion',
      headerName: 'Latest Version',
      flex: 1,
      valueGetter: (params) => params.row.latestVersion || '-',
    },
  ];

  /* ─────── render tabs ─────── */
  return (
    <Box m="20px">
      <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="data asset tabs">
        <Tab label="Available Data Assets" id="data-assets-tab-0" aria-controls="data-assets-tabpanel-0" sx={{ color: colors.primary[200], '&.Mui-selected': { color: colors.greenAccent[300] } }} />
        <Tab label="Contribution Stats" id="data-assets-tab-1" aria-controls="data-assets-tabpanel-1" sx={{ color: colors.primary[200], '&.Mui-selected': { color: colors.greenAccent[300] } }} />
        <Tab
          label="Release Delivery"
          id="data-assets-tab-2"
          aria-controls="data-assets-tabpanel-2"
          sx={{ color: colors.primary[200], '&.Mui-selected': { color: colors.greenAccent[300] } }}
        />
      </Tabs>

      {/* ─── Available Data Assets Tab ─── */}
      <TabPanel value={tab} index={0}>
        <Box display="flex" justifyContent="end" mt="20px">
          <Link to="/console/data/new" color="secondary" variant="contained">
            Add New Data Asset
          </Link>
        </Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap="30px">
          <Box>
            <Typography variant="h6">Real Time Services</Typography>
            <Box height="40vh" sx={{ '& .MuiDataGrid-root': { border: 'none' }, '& .MuiDataGrid-cell': { borderBottom: 'none' } }}>
              <DataGrid
                rows={realTimeServices}
                columns={columnsRealTime}
                getRowId={(row) => row.data_asset_id || row._id}
                disableSelectionOnClick
                onRowClick={(params) => setSelectedDataAssetId(params.row.data_asset_id || params.row._id)}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="h6">Asset View</Typography>
            <Box height="40vh" display="flex" justifyContent="center" alignItems="center">
              {selectedDataAssetId && isFetchingSelectedDataAsset && !selectedDataAsset ? (
                <Box textAlign="center" width="100%" color={colors.grey[300]}>
                  Loading selected asset...
                </Box>
              ) : isSelectedDataAssetError ? (
                <Typography variant="body2" color="error">
                  Error fetching selected asset: {
                    selectedDataAssetError?.data?.message ||
                    selectedDataAssetError?.error ||
                    'Unknown error'
                  }
                </Typography>
              ) : (
                <DataAssetInfo
                  dataAsset={selectedDataAsset || null}
                  onUpdated={() => {
                    refetch();
                    if (selectedDataAssetId) {
                      refetchSelectedDataAsset();
                    }
                  }}
                />
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="h6">External Delivery Assets</Typography>
            <Box height="40vh" sx={{ '& .MuiDataGrid-root': { border: 'none' }, '& .MuiDataGrid-cell': { borderBottom: 'none' } }}>
              <DataGrid
                rows={externalAssets}
                columns={columnsExternalInternal}
                getRowId={(row) => row.data_asset_id || row._id}
                disableSelectionOnClick
                onRowClick={(params) => setSelectedDataAssetId(params.row.data_asset_id || params.row._id)}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="h6">Internal Data Assets</Typography>
            <Box height="40vh" sx={{ '& .MuiDataGrid-root': { border: 'none' }, '& .MuiDataGrid-cell': { borderBottom: 'none' } }}>
              <DataGrid
                rows={internalAssets}
                columns={columnsExternalInternal}
                getRowId={(row) => row.data_asset_id || row._id}
                disableSelectionOnClick
                onRowClick={(params) => setSelectedDataAssetId(params.row.data_asset_id || params.row._id)}
              />
            </Box>
          </Box>
        </Box>
      </TabPanel>

      {/* ─── Contribution Stats Tab ─── */}
      <TabPanel value={tab} index={1}>
        <ContributionStats dataAssets={allDataAssets} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <ReleaseDeliveryTab />
      </TabPanel>
    </Box>
  );
};

export default DataAssetManagement;